import type { PropsWithChildren } from "react"
import { forwardRef } from "react"

import { Box, Stack, Typography, ButtonBase as MuiButtonBase } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { NumericFormat } from "react-number-format"

import { getPricePrefix } from "@ggs/utils"
import type { FC, ResponsiveImageType, TagProps } from ".."
import { Breadcrumbs, FCWC, SvgIcon, SpecialTag, WishlistButton } from ".."
import { LinkType } from "../../graphql/types"
import HeroMedia from "./HeroMedia"

type Hero2Props = {
  title: string
  image: ResponsiveImageType
  currency?: string
  duration: string
  specials?: {
    deadline: string | null
    savings: string
    addedValue: boolean | null
    specialOfferShortDescription: string | null
  }
  price: number
  maxPrice: number
  tags: TagProps[]
  path?: LinkType[]
  onClickTripCta?: () => void
  liked?: boolean | null
  onLike?: () => void
  onUnlike?: () => void
}

const Hero2: FC<Hero2Props> = ({
  title,
  image,
  duration,
  specials,
  price,
  maxPrice,
  path,
  currency,
  onClickTripCta,
  liked,
  onLike,
  onUnlike,
}) => {
  const prefix = getPricePrefix(currency as string)
  const showBookByDateAndSavings = specials?.savings && specials?.deadline
  const showBookByDateAndAddedValue = specials?.addedValue && specials?.deadline
  const showSpecialTag = showBookByDateAndSavings || showBookByDateAndAddedValue

  return (
    <HeroContainer>
      <HeroInner>
        {path && path.length > 0 && <Breadcrumbs items={path} />}
        <HeroMedia media={[image]} />
        <HeroContent>
          <Stack rowGap={{ xs: 6, md: 10 }}>
            {showSpecialTag && (
              <SpecialTag
                bookDate={specials?.deadline!}
                savings={specials?.savings}
                addedValue={specials?.addedValue}
                specialOfferShortDescription={specials?.specialOfferShortDescription!}
                sx={{
                  position: "relative",
                  top: "unset",
                  left: "unset",
                  width: "fit-content",
                }}
              />
            )}
            <Box position="absolute" top={{ xs: 16, md: 32 }} right={{ xs: 16, md: 32 }} zIndex={3}>
              <WishlistButton liked={liked} onLike={onLike} onUnlike={onUnlike} />
            </Box>
            <HeroTitle title={title} />
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" mt={{ xs: 2, md: 6 }}>
              <HeroInfo duration={duration} price={price} maxPrice={maxPrice} prefix={prefix} />
              <HeroCallToAction onClick={onClickTripCta} />
            </Stack>
          </Stack>
        </HeroContent>
      </HeroInner>
    </HeroContainer>
  )
}
export default Hero2

/** A sub-component for the footer content of the hero content */
const HeroInfo: FC<Pick<Hero2Props, "duration" | "price" | "maxPrice"> & { prefix: string }> = ({
  duration,
  price,
  maxPrice,
  prefix,
}) => {
  const isPriceAvailable = price && price > 200

  // showing max price if available
  const getThreshold = (rawPrice: number) => {
    if (rawPrice <= 5000) return 0.1
    if (rawPrice <= 10000) return 0.08
    if (rawPrice <= 15000) return 0.07
    return 0.06
  }
  const shouldShowRange = (rawPrice: number, rawMaxPrice: number) => {
    const threshold = getThreshold(rawPrice)
    const difference = rawMaxPrice - rawPrice
    const percentDiff = difference / rawPrice
    return percentDiff >= threshold
  }
  const showRange = shouldShowRange(price, maxPrice)

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={{ xs: 0, md: 2 }}
      alignItems={{ xs: "flex-start", md: "center" }}
    >
      {duration && (
        <Typography variant="body.md" fontSize="18px" fontWeight={600} textTransform="uppercase">
          {duration}
        </Typography>
      )}
      {isPriceAvailable ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span
            style={{ 
              fontSize: "14px", 
              fontStyle: "normal", 
              fontWeight: 600, 
              lineHeight: "140%" 
            }}
          >
            {showRange ? (
              <>
                <NumericFormat
                  value={price}
                  displayType="text"
                  thousandSeparator
                  prefix={prefix}
                  decimalScale={0}
                />
                {" - "}
                <NumericFormat
                  value={maxPrice}
                  displayType="text"
                  thousandSeparator
                  prefix={prefix}
                  decimalScale={0}
                />
              </>
            ) : (
              <NumericFormat
                value={price}
                displayType="text"
                thousandSeparator
                prefix={prefix}
                decimalScale={0}
              />
            )}
          </span>
          <span
            style={{
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "140%",
              textTransform: "uppercase",
            }}
          >
            / p.p.
          </span>
        </Box>
      ) : (
        <Typography
          variant="body.md"
          sx={{
            fontSize: "14px!important",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "140%",
          }}
        >
          Please Inquire
        </Typography>
      )}
    </Stack>
  )
}

const HeroCallToAction: FC<{ onClick?: () => void }> = ({ onClick }) => {
  return (
    <MuiButtonBase
      onClick={onClick}
      sx={(theme) => ({
        ...theme.typography["link.md"],
        "display": "flex",
        "alignItems": "center",
        "gap": 2,
        "minHeight": 56,
        "padding": theme.spacing(3, 6),
        "backgroundColor": theme.palette["primary.800"].main,
        "borderRadius": 1,
        "lineHeight": 1,
        "transition": theme.transitions.create(["background-color", "border-color", "color"]),
        "width": { xs: "100%", md: "auto" },
        "justifyContent": "center",
        "textAlign": "center",
        "boxSizing": "border-box",
        "marginTop": { xs: 2, md: 0 },
        "marginLeft": { xs: 0, md: 2 },
        "&:hover": {
          backgroundColor: theme.palette["primary.900"].main,
        },
      })}
    >
      <Typography
        variant="body.md"
        fontWeight={600}
        fontSize="18px"
        sx={(theme) => ({
          color: theme.palette["neutral.100"].main,
        })}
      >
        Start Your Quote
      </Typography>
      <SvgIcon
        viewBox="0 0 17 16"
        variant="arrowRight"
        strokeMode
        size={18}
        sx={(theme) => ({
          transition: theme.transitions.create(["transform"]),
        })}
      />
    </MuiButtonBase>
  )
}

/** A sub-component for the section container of the hero content */
const HeroContainer = forwardRef<HTMLDivElement, PropsWithChildren>(
  ({ children, ...props }, ref) => (
    <Stack
      ref={ref}
      component="header"
      alignItems="stretch"
      justifyContent="flex-end"
      width="100%"
      position="relative"
      top="0"
      pt={{ xs: 16, md: 32.5 }}
      sx={{ isolation: "isolate" }}
      zIndex={1}
      {...props}
    >
      {children}
    </Stack>
  )
)
HeroContainer.displayName = "HeroSection"

/** A sub-component for the section container of the hero content */
const HeroInner: FCWC = ({ children }) => (
  <Stack
    height={{ xs: 570, md: 670 }}
    padding={{ xs: 2, md: 2.5 }}
    sx={{
      isolation: "isolate",
      position: "relative",
    }}
  >
    <Stack
      justifyContent="flex-end"
      height="100%"
      position="relative"
      sx={{ borderRadius: { md: 1.5 } }}
    >
      {children}
    </Stack>
  </Stack>
)

/** A sub-component for the content of the hero content */
const HeroContent: FCWC = ({ children }) => (
  <Box p={{ xs: 2, md: 8 }} zIndex={2}>
    <Stack
      width="100%"
      maxWidth={{ md: 687 }}
      bgcolor={(theme) => alpha(theme.palette["secondary.800"].main, 0.8)}
      borderRadius={1.5}
      boxShadow={(theme) => `0 0 8px ${alpha(theme.palette["secondary.800"].main, 0.1)}`}
      color={(theme) => theme.palette["neutral.100"].main}
      padding={{ xs: 4, md: 8 }}
      sx={{ backdropFilter: "blur(3px)" }}
    >
      {children}
    </Stack>
  </Box>
)

/** A sub-component for the title content of the hero content */
const HeroTitle: FC<Pick<Hero2Props, "title">> = ({ title }) => (
  <Box>
    <Typography
      variant="heading.xl"
      component="h1"
      color={(theme) => theme.palette["neutral.100"].main}
      fontSize={{ md: 44 }}
      pr={5}
    >
      {title}
    </Typography>
  </Box>
)
